/**
 * Central route registry for workspace-scoped department URL structure.
 *
 * URL pattern: /workspaces/{wsSlug}/departments/{dept}/{team}
 *
 * Every frontend route that targets a department should import
 * path builders from this file instead of hardcoding strings.
 */

import type { WorkflowType } from "@/features/department";

// ── Slug types ──────────────────────────────────────────────────────────────

export type DepartmentSlug = "marketing" | "creative" | "businessops" | "strategy";

export type MarketingTeam = "geo" | "ads" | "social" | "content";
export type CreativeTeam = "products" | "brand-dna" | "avatars" | "voice" | "images" | "videos";
export type BusinessOpsTeam = "analytics" | "forge" | "support" | "supply" | "finance";
export type StrategyTeam = "forge";
export type TeamSlug = MarketingTeam | CreativeTeam | BusinessOpsTeam | StrategyTeam;

// ── Path builders ───────────────────────────────────────────────────────────

export function deptHub(wsSlug: string) {
  return `/workspaces/${wsSlug}/departments` as const;
}

export function deptPage(wsSlug: string, dept: DepartmentSlug) {
  return `/workspaces/${wsSlug}/departments/${dept}` as const;
}

export function teamPage(wsSlug: string, dept: DepartmentSlug, team: TeamSlug) {
  return `/workspaces/${wsSlug}/departments/${dept}/${team}` as const;
}

export function contentSourcePage(wsSlug: string, sourceId: string) {
  return `/workspaces/${wsSlug}/departments/marketing/content/source/${sourceId}`;
}

export function contentArticlePage(wsSlug: string, articleId: string) {
  return `/workspaces/${wsSlug}/departments/marketing/content/article/${articleId}`;
}

function normalizeLegacySlug(slug: string | null | undefined): string {
  return (slug ?? "").trim().toLowerCase();
}

// ── WorkflowType → department/team mapping ───────────────────────────────────

export const COAGENT_TYPE_TO_DEPT_TEAM: Record<string, { dept: DepartmentSlug; team: TeamSlug }> = {
  // Canonical — coagent_type = team slug
  geo: { dept: "marketing", team: "geo" },
  ads: { dept: "marketing", team: "ads" },
  content: { dept: "marketing", team: "content" },
  "brand-dna": { dept: "creative", team: "brand-dna" },
  products: { dept: "creative", team: "products" },
  forge: { dept: "businessops", team: "forge" },
  // Legacy aliases
  growth: { dept: "marketing", team: "ads" },
  research: { dept: "marketing", team: "ads" },
  brand: { dept: "creative", team: "brand-dna" },
  ecommerce: { dept: "creative", team: "products" },
  social: { dept: "marketing", team: "social" },
};

// ── Legacy flat slug / flow slug → department/team mapping ──────────���────────

export const LEGACY_SLUG_TO_DEPT_TEAM: Record<string, { dept: DepartmentSlug; team: TeamSlug }> = {
  // Old agentSlug values from coagentRoute.ts COAGENT_ROUTE_SLUG
  "performance-marketing": { dept: "marketing", team: "ads" },
  "social-growth": { dept: "marketing", team: "social" },
  "brand-designer": { dept: "creative", team: "brand-dna" },
  googlemarketing: { dept: "marketing", team: "ads" },
  "geo-audit": { dept: "marketing", team: "geo" },
  ecommerce: { dept: "creative", team: "products" },

  // Direct type-style slugs that may appear
  brand: { dept: "creative", team: "brand-dna" },
  geo: { dept: "marketing", team: "geo" },
  growth: { dept: "marketing", team: "ads" },
  research: { dept: "marketing", team: "ads" },
  social: { dept: "marketing", team: "social" },

  // Ads workspace slug
  ads: { dept: "marketing", team: "ads" },

  // Historical flow slugs from hub/subflow routing
  "seo-blog": { dept: "marketing", team: "content" },
  "email-marketing": { dept: "marketing", team: "social" },
  "creative-studio": { dept: "creative", team: "videos" },
  products: { dept: "creative", team: "products" },
  "brand-design": { dept: "creative", team: "brand-dna" },
  "brand-dna": { dept: "creative", team: "brand-dna" },
  avatars: { dept: "creative", team: "avatars" },
  videos: { dept: "creative", team: "videos" },
  content: { dept: "marketing", team: "content" },

  // Forge
  forge: { dept: "businessops", team: "forge" },
  "forge-copilot": { dept: "businessops", team: "forge" },

  // Common legacy aliases
  "business-ops": { dept: "businessops", team: "analytics" },
  avatar: { dept: "creative", team: "avatars" },
};

// ── Old hub agent ID → department mapping (for ?agent= query params) ────────

export const LEGACY_AGENT_ID_TO_DEPT: Record<string, DepartmentSlug> = {
  "performance-marketing": "marketing",
  "social-growth": "creative",
  "brand-designer": "businessops",
  nova: "marketing",
  operator: "marketing",
  cipher: "creative",
  remi: "businessops",
};

/**
 * Backward-compatible aliases retained for existing imports.
 * Prefer LEGACY_* names in new code.
 */
export const OLD_SLUG_TO_DEPT_TEAM = LEGACY_SLUG_TO_DEPT_TEAM;
export const OLD_AGENT_ID_TO_DEPT = LEGACY_AGENT_ID_TO_DEPT;

export type LegacyRouteWarningCode =
  | "unknown_legacy_slug"
  | "unknown_legacy_flow"
  | "unknown_legacy_agent";

export type LegacyRouteResolution = {
  path: string;
  normalizedInput?: string;
  warningCode?: LegacyRouteWarningCode;
};

function getLegacyTeamMapping(slug: string | null | undefined) {
  const normalized = normalizeLegacySlug(slug);
  if (!normalized) return null;
  return LEGACY_SLUG_TO_DEPT_TEAM[normalized] ?? null;
}

// ── Resolve legacy slug/agent routes to canonical department URLs ───────────

export function resolveLegacySlugPath(
  slug: string | null | undefined,
  wsSlug?: string,
): LegacyRouteResolution {
  const normalized = normalizeLegacySlug(slug);
  const mapping = getLegacyTeamMapping(normalized);
  const hub = wsSlug ? deptHub(wsSlug) : "/departments";
  if (mapping) {
    return {
      path: wsSlug
        ? teamPage(wsSlug, mapping.dept, mapping.team)
        : `/departments/${mapping.dept}/${mapping.team}`,
      normalizedInput: normalized,
    };
  }
  return normalized
    ? {
        path: hub,
        normalizedInput: normalized,
        warningCode: "unknown_legacy_slug",
      }
    : { path: hub };
}

export function resolveLegacyAgentDetailPath(input: {
  agentId?: string | null;
  flowSlug?: string | null;
  wsSlug?: string | null;
}): LegacyRouteResolution {
  const wsSlug = input.wsSlug ?? undefined;
  const normalizedFlow = normalizeLegacySlug(input.flowSlug);
  if (normalizedFlow) {
    const flowMapping = getLegacyTeamMapping(normalizedFlow);
    if (flowMapping) {
      return {
        path: wsSlug
          ? teamPage(wsSlug, flowMapping.dept, flowMapping.team)
          : `/departments/${flowMapping.dept}/${flowMapping.team}`,
        normalizedInput: normalizedFlow,
      };
    }
  }

  const normalizedAgent = normalizeLegacySlug(input.agentId);
  if (normalizedAgent) {
    const dept = LEGACY_AGENT_ID_TO_DEPT[normalizedAgent];
    if (dept) {
      return {
        path: wsSlug ? deptPage(wsSlug, dept) : `/departments/${dept}`,
        normalizedInput: normalizedAgent,
        warningCode: normalizedFlow ? "unknown_legacy_flow" : undefined,
      };
    }

    const teamMapping = getLegacyTeamMapping(normalizedAgent);
    if (teamMapping) {
      return {
        path: wsSlug
          ? teamPage(wsSlug, teamMapping.dept, teamMapping.team)
          : `/departments/${teamMapping.dept}/${teamMapping.team}`,
        normalizedInput: normalizedAgent,
        warningCode: normalizedFlow ? "unknown_legacy_flow" : undefined,
      };
    }
  }

  const hub = wsSlug ? deptHub(wsSlug) : "/departments";
  return {
    path: hub,
    normalizedInput: normalizedFlow || normalizedAgent || undefined,
    warningCode: normalizedFlow
      ? "unknown_legacy_flow"
      : normalizedAgent
        ? "unknown_legacy_agent"
        : undefined,
  };
}

export function legacySlugToPath(slug: string, wsSlug?: string): string {
  return resolveLegacySlugPath(slug, wsSlug).path;
}

// ── Resolve WorkflowType to a full department path ───────────────────────────

export function coAgentTypeToDeptPath(wsSlug: string, coAgentType: WorkflowType): string {
  const mapping = COAGENT_TYPE_TO_DEPT_TEAM[coAgentType];
  return teamPage(wsSlug, mapping.dept, mapping.team);
}

/**
 * Resolve a department path from session/agent data.
 * Handles WorkflowType, old agentStyle slugs, and legacy overrides.
 */
export function resolveDepartmentPathFromSessionInput(input: {
  coAgentType?: unknown;
  agentStyle?: unknown;
  wsSlug?: unknown;
}): string {
  const wsSlug = (typeof input.wsSlug === "string" && input.wsSlug.trim()) || undefined;
  const hub = wsSlug ? deptHub(wsSlug) : "/departments";

  if (
    typeof input.coAgentType === "string" &&
    input.coAgentType in COAGENT_TYPE_TO_DEPT_TEAM &&
    wsSlug
  ) {
    return coAgentTypeToDeptPath(wsSlug, input.coAgentType as WorkflowType);
  }

  if (typeof input.agentStyle === "string" && input.agentStyle.trim()) {
    return resolveLegacySlugPath(input.agentStyle, wsSlug).path;
  }

  return hub;
}

/** Convenience alias for resolveDepartmentPathFromSessionInput. */
export const getDepartmentPath = resolveDepartmentPathFromSessionInput;
